import {
  DFData,
  DField,
  DFieldType,
  DFValues,
  DSearchSelectField,
  DSelectField
} from '@vmware-react/vrlcm';
import { l10n } from 'i18n/i18nUtil';
import { oc } from 'ts-optchain';
import {
  GroupMetadata,
  PropertyMetadata,
  SNMPConfiguration
} from 'vrlcm-gui-types';
import * as yup from 'yup';
import {
  FIELD_HELP_TEXT_MAPPING,
  FIELD_MAPPING,
  FIELD_PLACEHOLDER_MAPPING,
  LOCKER_PROPERTIES,
  PASSWORD_PROPERTIES
} from '../../../../constants/AppConstants';
import { AliasVmid } from '../../../../types/Common';
import switchTypes from './switch-types';
import { NSXTManager } from './types';

export const DERIVED_PROXY_IP_NODES = ['pks', 'kubernetes', 'openshift'];

const CHECKBOX_PROPERTIES = ['netflow'];

const SELECT_PROPERTIES = [
  ...LOCKER_PROPERTIES,
  'proxyIP',
  'vCenterIp',
  'switchType',
  'dsManagerIp'
];

export const REQUIRED_PROPERTIES = [
  'dsManagerIp',
  'proxyIP',
  'dsIp',
  'dsUsername',
  'dsPassword',
  'dsNickname'
];

export const SNMP_PROPERTIES = ['snmpEnabled', 'snmpSelected'];

export function getSchema(groupMetadata?: GroupMetadata, values?: DFValues) {
  return yup.object().shape(
    groupMetadata
      ? groupMetadata.properties
          .filter(prop => CHECKBOX_PROPERTIES.indexOf(prop.name) < 0)
          .reduce((acc, prop) => {
            return {
              ...acc,
              [prop.name]: getPropertySchema(prop.name, values)
            };
          }, {})
      : {}
  );
}

export function toDFData(
  initialValues: DFValues,
  groupMetadata: GroupMetadata,
  collectorIps: string[],
  vcIps: string[],
  cloudVCIps: string[],
  nsxtManagers: NSXTManager[],
  passAliases: AliasVmid[],
  snmpConfigurations: SNMPConfiguration[],
  isVrniGreaterThanEqualTo690: boolean
): DFData {
  return {
    fields:
      typeof groupMetadata !== 'undefined'
        ? getSortedProperties(groupMetadata.properties).map(
            toDField(
              groupMetadata.name,
              initialValues,
              collectorIps,
              vcIps,
              cloudVCIps,
              nsxtManagers,
              passAliases,
              snmpConfigurations,
              isVrniGreaterThanEqualTo690
            )
          )
        : [],
    orientation: 'horizontal'
  };
}

function getSortedProperties(properties: PropertyMetadata[]) {
  const requiredProperties = properties
    .filter(p => REQUIRED_PROPERTIES.includes(p.name))
    .slice()
    .sort(
      (a, b) =>
        REQUIRED_PROPERTIES.indexOf(a.name) -
        REQUIRED_PROPERTIES.indexOf(b.name)
    );
  const optionalProperties = properties.filter(
    p =>
      !REQUIRED_PROPERTIES.includes(p.name) && !SNMP_PROPERTIES.includes(p.name)
  );
  const snmpProperties = properties
    .filter(p => SNMP_PROPERTIES.includes(p.name))
    .slice()
    .sort(
      (a, b) =>
        SNMP_PROPERTIES.indexOf(a.name) - SNMP_PROPERTIES.indexOf(b.name)
    );
  return [...requiredProperties, ...optionalProperties, ...snmpProperties];
}

function toDField(
  nodeType: string,
  initialValues: DFValues,
  collectorIps: string[],
  vcIps: string[],
  cloudVCIps: string[],
  nsxtManagers: NSXTManager[],
  passAliases: AliasVmid[],
  snmpConfigurations: SNMPConfiguration[],
  isVrniGreaterThanEqualTo690: boolean
) {
  return ({ name, dataType }: PropertyMetadata): DField => {
    let initialValue = oc(initialValues)[name]('');

    if (DERIVED_PROXY_IP_NODES.includes(nodeType) && name === 'proxyIP') {
      const dsManagerIp = oc(initialValues).dsManagerIp('') as string;
      if (dsManagerIp.length > 0) {
        const nsxtManager = nsxtManagers.find(m => m.ip === dsManagerIp);
        initialValue = nsxtManager ? nsxtManager.proxyIp : '';
      } else {
        initialValue = '';
      }
    }

    const field = {
      name,
      initialValue,
      type: getType(name, dataType),
      label: FIELD_MAPPING[name] || name,
      placeholder: FIELD_PLACEHOLDER_MAPPING[name],
      helperText: FIELD_HELP_TEXT_MAPPING[name],
      required: REQUIRED_PROPERTIES.includes(name)
    };

    if (name === 'proxyIP') {
      const fieldCollectorIps =
        typeof initialValue === 'string' && initialValue.length > 0
          ? Array.from(new Set([...collectorIps, initialValue]).values())
          : collectorIps;
      return {
        ...field,
        options: fieldCollectorIps.map(ip => ({
          textLabel: ip,
          value: ip
        })),
        disabled: DERIVED_PROXY_IP_NODES.includes(nodeType)
      } as DSelectField;
    } else if (name === 'vCenterIp') {
      const vCenterIps = nodeType === 'policymanager' ? cloudVCIps : vcIps;
      return {
        ...field,
        options: vCenterIps.map(ip => ({
          textLabel: ip,
          value: ip
        }))
      } as DSelectField;
    } else if (name === 'dsManagerIp') {
      return {
        ...field,
        options: nsxtManagers.map(({ ip }) => ({
          textLabel: ip,
          value: ip
        }))
      } as DSelectField;
    } else if (name === 'switchType') {
      const filteredSwitchTypes = isVrniGreaterThanEqualTo690
        ? switchTypes
        : switchTypes.filter(s => s.label !== 'Cisco Catalyst 9300');
      return {
        ...field,
        options: filteredSwitchTypes.map(({ label: textLabel, value }) => ({
          textLabel,
          value
        }))
      } as DSelectField;
    } else if (PASSWORD_PROPERTIES.includes(name)) {
      return {
        ...field,
        placeholder: l10n('form.input.select', [FIELD_MAPPING[name]]),
        options: passAliases.map(({ alias, vmid }) => ({
          textLabel: alias,
          value: `locker:password:${vmid}:${alias}`
        }))
      } as DSearchSelectField;
    } else if (name === 'snmpSelected') {
      return {
        ...field,
        required: true,
        options: snmpConfigurations.map(c => ({
          textLabel: c.snmpName,
          value: c.snmpName
        })),
        defaultValue: '',
        displayRules: [
          {
            fieldRef: 'snmpEnabled',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ]
      } as DSelectField;
    }

    return field as DField;
  };
}

function getType(name: string, dataType: string): DFieldType {
  if (PASSWORD_PROPERTIES.includes(name)) {
    return 'searchable';
  } else if (SELECT_PROPERTIES.indexOf(name) >= 0) {
    return 'select';
  } else if (CHECKBOX_PROPERTIES.indexOf(name) >= 0) {
    return 'checkbox';
  } else {
    switch (dataType) {
      case 'vm.lcm.radiogroup':
        return 'radio';
      case 'vm.lcm.checkbox':
      case 'vm.lcm.toggle':
        return 'checkbox';
      case 'vm.lcm.password':
        return 'searchable';
      case 'vm.lcm.certificate':
      case 'vm.lcm.locker':
      case 'vm.lcm.select':
        return 'select';
      case 'vm.lcm.textarea':
        return 'textarea';
      default:
        return 'text';
    }
  }
}

function getPropertySchema(name: string, values?: DFValues) {
  if (
    REQUIRED_PROPERTIES.includes(name) ||
    (name === 'snmpSelected' && values && String(values.snmpEnabled) === 'true')
  ) {
    return yup
      .string()
      .required(l10n('form.input.isRequired', [FIELD_MAPPING[name] || name]));
  }
  return yup.string();
}
